/**
 * Authentication API utilities
 */

import { LoginRequest, LoginResponse, RegisterRequest, RegisterResponse, User } from '@/types/auth';

const BACKEND_URL = process.env.NEXT_PUBLIC_API_BACKEND || 'https://sca-backend.digitaldevworkspace.com';

/**
 * Login user
 */
export async function loginUser(credentials: LoginRequest): Promise<LoginResponse> {
  console.log('🔍 [Auth API] Login request:', { email: credentials.email });
  
  try {
    const response = await fetch(`${BACKEND_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    console.log('🔍 [Auth API] Login response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ [Auth API] Login failed:', response.status, errorData);
      throw new Error(errorData.message || `Login failed: ${response.statusText}`);
    }

    const responseData = await response.json();
    console.log('🔍 [Auth API] Raw response:', responseData);
    
    // Handle nested response structure
    const data: LoginResponse = {
      accessToken: responseData.data.accessToken,
      tokenType: responseData.data.tokenType,
      expiresIn: responseData.data.expiresIn,
      user: responseData.data.user
    };
    
    console.log('✅ [Auth API] Login successful:', { 
      user: data.user.email, 
      role: data.user.role,
      isActive: data.user.isActive 
    });

    return data;
  } catch (error) {
    console.error('❌ [Auth API] Login error:', error);
    throw error;
  }
}

/**
 * Register user
 */
export async function registerUser(userData: RegisterRequest): Promise<RegisterResponse> {
  console.log('🔍 [Auth API] Register request:', { email: userData.email, firstName: userData.firstName });
  
  try {
    const response = await fetch(`${BACKEND_URL}/api/v1/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    console.log('🔍 [Auth API] Register response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ [Auth API] Register failed:', response.status, errorData);
      throw new Error(errorData.message || `Registration failed: ${response.statusText}`);
    }

    const responseData = await response.json();
    console.log('🔍 [Auth API] Raw register response:', responseData);
    
    // Handle nested response structure for registration
    const data: RegisterResponse = {
      message: responseData.message || 'Registration successful',
      success: responseData.success || true
    };
    
    console.log('✅ [Auth API] Register successful:', { 
      success: data.success, 
      message: data.message 
    });

    return data;
  } catch (error) {
    console.error('❌ [Auth API] Register error:', error);
    throw error;
  }
}

/**
 * Verify token
 */
export async function verifyToken(token: string): Promise<User> {
  console.log('🔍 [Auth API] Verifying token...');
  
  try {
    const response = await fetch(`${BACKEND_URL}/api/v1/auth/verify`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    console.log('🔍 [Auth API] Verify response status:', response.status);

    if (!response.ok) {
      throw new Error('Token verification failed');
    }

    const responseData = await response.json();
    console.log('🔍 [Auth API] Raw verify response:', responseData);
    
    // Handle nested response structure for token verification
    const user = responseData.data?.user || responseData.user;
    
    console.log('✅ [Auth API] Token verification successful:', { 
      user: user?.email, 
      role: user?.role 
    });

    return user;
  } catch (error) {
    console.error('❌ [Auth API] Token verification error:', error);
    throw error;
  }
}

/**
 * Register organization with user
 */
export async function registerOrganization(data: any): Promise<any> {
  console.log('🔍 [Auth API] Organization registration request:', { organisationName: data.organisationName });
  
  try {
    const response = await fetch(`${BACKEND_URL}/api/v1/organisations/public/create-with-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    console.log('📡 [Auth API] Organization registration response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ [Auth API] Organization registration failed:', errorData);
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();
    console.log('✅ [Auth API] Organization registration successful:', responseData);
    return responseData;
  } catch (error) {
    console.error('❌ [Auth API] Organization registration error:', error);
    throw error;
  }
}

/**
 * Get current user profile
 */
export async function getCurrentUser(token: string): Promise<User> {
  console.log('🔍 [Auth API] Getting current user profile...');
  
  try {
    const response = await fetch(`${BACKEND_URL}/api/v1/auth/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    console.log('🔍 [Auth API] Me response status:', response.status);

    if (!response.ok) {
      throw new Error('Failed to get user profile');
    }

    const responseData = await response.json();
    console.log('🔍 [Auth API] Raw me response:', responseData);
    
    // Handle nested response structure - user data is at data.data
    let user;
    if (responseData.data && responseData.data.data) {
      user = responseData.data.data;
    } else if (responseData.data && responseData.data.user) {
      user = responseData.data.user;
    } else if (responseData.data) {
      user = responseData.data;
    } else {
      user = responseData;
    }
    
    console.log('🔍 [Auth API] Extracted user data:', user);
    console.log('✅ [Auth API] User profile retrieved:', { 
      user: user?.email, 
      role: user?.role,
      firstName: user?.firstName,
      lastName: user?.lastName
    });

    return user;
  } catch (error) {
    console.error('❌ [Auth API] Get user profile error:', error);
    throw error;
  }
}

/**
 * Forgot Password - request PIN to be sent to email
 */
export async function requestPasswordReset(email: string): Promise<{ success: boolean; message: string }>{
  try {
    const response = await fetch(`${BACKEND_URL}/api/v1/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to request password reset');
    }
    return { success: true, message: data?.message || 'If this email exists, a PIN has been sent.' };
  } catch (error: any) {
    return { success: false, message: error?.message || 'Failed to request password reset' };
  }
}

/**
 * Reset Password with PIN token
 */
export async function resetPassword(token: string, password: string): Promise<{ success: boolean; message: string }>{
  try {
    const response = await fetch(`${BACKEND_URL}/api/v1/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to reset password');
    }
    return { success: true, message: data?.message || 'Password updated successfully' };
  } catch (error: any) {
    return { success: false, message: error?.message || 'Failed to reset password' };
  }
}

/**
 * Email Verification - request PIN to be sent to user's email
 */
export async function requestEmailVerification(email: string): Promise<{ success: boolean; message: string }>{
  try {
    const response = await fetch(`${BACKEND_URL}/api/v1/auth/request-email-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to request email verification');
    }
    return { success: true, message: data?.message || 'Verification PIN sent to your email.' };
  } catch (error: any) {
    return { success: false, message: error?.message || 'Failed to request email verification' };
  }
}

/**
 * Email Verification - confirm with PIN token
 */
export async function confirmEmailVerification(email: string, code: string): Promise<{ success: boolean; message: string }>{
  try {
    const response = await fetch(`${BACKEND_URL}/api/v1/auth/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to verify email');
    }
    return { success: true, message: data?.message || 'Email verified successfully' };
  } catch (error: any) {
    return { success: false, message: error?.message || 'Failed to verify email' };
  }
}
